import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Card, CardContent, Box, Typography, Chip, LinearProgress, ToggleButtonGroup, ToggleButton } from '@mui/material';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import TimelineOutlinedIcon from '@mui/icons-material/TimelineOutlined';
import GridViewIcon from '@mui/icons-material/GridView';
// import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
// import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

// Utility function for node and chip colors
const getNodeColor = (sentiment) => {
  switch (sentiment) {
    case 'positive':
      return '#4caf50'; // green
    case 'neutral':
      return '#ffeb3b'; // yellow
    case 'negative':
      return '#f44336'; // red
    default:
      return '#9e9e9e'; // grey
  }
};

const ClusterSection = ({ clusters, maxFrequency, showQuotes }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
    {clusters.map((cluster, index) => (
      <Box key={index}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {cluster.name}
            </Typography>
            <Chip
              label={cluster.sentiment}
              size="small"
              variant="outlined"
              sx={{
                height: 20,
                fontSize: '0.75rem',
                bgcolor:
                  cluster.sentiment === 'negative'
                    ? 'error.lighter'
                    : cluster.sentiment === 'positive'
                    ? 'success.lighter'
                    : 'warning.lighter',
                color: cluster.sentiment === 'negative' ? 'error.dark' : cluster.sentiment === 'positive' ? 'success.dark' : 'warning.dark',
                borderColor:
                  cluster.sentiment === 'negative' ? 'error.light' : cluster.sentiment === 'positive' ? 'success.light' : 'warning.light'
              }}
            />
            <Chip
              label={cluster.business_impact}
              size="small"
              variant="outlined"
              sx={{
                height: 20,
                fontSize: '0.75rem',
                bgcolor: cluster.business_impact === 'high' ? 'grey.100' : 'grey.50',
                color: cluster.business_impact === 'high' ? 'grey.800' : 'grey.600',
                borderColor: cluster.business_impact === 'high' ? 'grey.300' : 'grey.200'
              }}
            />
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ flexGrow: 1 }}>
            <LinearProgress
              variant="determinate"
              value={(cluster.frequency / maxFrequency) * 100}
              sx={{
                height: 8,
                borderRadius: 1,
                bgcolor: 'grey.100',
                '& .MuiLinearProgress-bar': {
                  bgcolor: 'grey.600',
                  borderRadius: 1
                }
              }}
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 56 }}>
            <Typography variant="caption" sx={{ fontWeight: 500 }}>
              {cluster.frequency}
            </Typography>
          </Box>
        </Box>

        {showQuotes && (
          <Box
            sx={{
              mt: 1,
              ml: 2,
              pl: 2,
              borderLeft: '2px solid',
              borderColor: 'grey.200',
              display: 'flex',
              flexDirection: 'column',
              gap: 1
            }}
          >
            {cluster.key_quotes.map((quote, quoteIndex) => (
              <Typography key={quoteIndex} variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                &quot;{quote}&quot;
              </Typography>
            ))}
          </Box>
        )}
      </Box>
    ))}
  </Box>
);

const ClusterCard = ({ cluster, position, isTopMost, onExpand, showQuotes }) => {
  const [isExpanded, setIsExpanded] = useState(true);

  const handleToggle = (e) => {
    e.stopPropagation();
    setIsExpanded(!isExpanded);
    if (!isExpanded) {
      onExpand();
    }
  };

  return (
    <Box
      onClick={handleToggle}
      sx={{
        position: 'absolute',
        backgroundColor: 'background.paper',
        border: '1px solid #e0e0e0',
        borderRadius: '8px',
        boxShadow: isExpanded ? 1 : 1,
        p: 1.5,
        width: '125px',
        zIndex: isExpanded ? (isTopMost ? 1200 : 1100) : 1,
        transition: 'all 0.2s ease-in-out',
        cursor: 'pointer',
        '&:hover': {
          boxShadow: isExpanded ? 4 : 3,
          borderColor: 'divider'
        },
        ...(position === 'bottom'
          ? {
              top: '30px',
              left: '50%',
              transform: 'translateX(-50%)'
            }
          : position === 'top'
          ? {
              bottom: '30px',
              left: '50%',
              transform: 'translateX(-50%)'
            }
          : position === 'left'
          ? {
              right: '30px',
              top: '50%',
              transform: 'translateY(-50%)'
            }
          : {
              // right
              left: '30px',
              top: '50%',
              transform: 'translateY(-50%)'
            })
      }}
    >
      {/* Frequency label */}
      <Box
        sx={{
          position: 'absolute',
          bgcolor: 'background.paper',
          fontSize: '0.75rem',
          fontWeight: 'bold',
          px: 0.75,
          py: 0.25,
          borderRadius: '9999px',
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: 1,
          zIndex: 20,
          transform: 'translateX(-50%)',
          left: '50%',
          ...(cluster.sentiment === 'positive' ? { top: '-24px' } : { bottom: '-24px' })
        }}
      >
        {cluster.frequency}
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%'
        }}
      >
        {/* Header - Always visible */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flex: 1,
            minWidth: 0
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              maxWidth: '100%',
              wordBreak: 'break-word'
            }}
          >
            {cluster.name}
          </Typography>
        </Box>

        {/* Expandable content */}
        <Box
          sx={{
            maxHeight: isExpanded ? '400px' : '0px',
            overflow: 'hidden',
            transition: 'all 0.2s ease-in-out'
          }}
        >
          {/* Tags section */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              flexWrap: 'wrap',
              gap: 0.5,
              mb: 1
            }}
          >
            <Chip
              label={cluster.sentiment}
              size="small"
              onClick={(e) => e.stopPropagation()}
              sx={{
                height: 20,
                fontSize: '0.75rem',
                bgcolor: getNodeColor(cluster.sentiment),
                color: '#fff'
              }}
            />
            <Chip
              label={cluster.business_impact}
              size="small"
              onClick={(e) => e.stopPropagation()}
              sx={{
                height: 20,
                fontSize: '0.75rem',
                bgcolor: cluster.business_impact === 'high' ? 'grey.100' : 'grey.50',
                color: cluster.business_impact === 'high' ? 'grey.800' : 'grey.600'
              }}
            />
          </Box>

          {/* Quotes section */}
          {showQuotes && (
            <Box>
              {cluster.key_quotes.map((quote, index) => (
                <Typography
                  key={index}
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    display: 'block',
                    mb: 0.5,
                    fontStyle: 'italic'
                  }}
                >
                  &quot;{quote}&quot;
                </Typography>
              ))}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

const MatrixView = ({ clusters, maxFrequency, showQuotes }) => {
  const [lastExpandedId, setLastExpandedId] = useState(null);

  // Sort clusters by frequency for proper distribution
  const sortedClusters = [...clusters].sort((a, b) => a.frequency - b.frequency);

  // Calculate frequency ranges
  const getFrequencyRange = (frequency) => {
    const lowThreshold = maxFrequency * 0.33;
    const highThreshold = maxFrequency * 0.66;

    if (frequency <= lowThreshold) return 'low';
    if (frequency <= highThreshold) return 'medium';
    return 'high';
  };

  // Group clusters by frequency range
  const groupedClusters = sortedClusters.reduce(
    (acc, cluster) => {
      const range = getFrequencyRange(cluster.frequency);
      if (!acc[range]) acc[range] = [];
      acc[range].push(cluster);
      return acc;
    },
    { low: [], medium: [], high: [] }
  );

  // Calculate section widths based on number of items
  const totalItems = clusters.length;
  const distribution = {
    low: (groupedClusters.low.length / totalItems) * 100,
    medium: (groupedClusters.medium.length / totalItems) * 100,
    high: (groupedClusters.high.length / totalItems) * 100
  };

  // Calculate cumulative widths for positioning
  const cumulativeWidth = {
    low: 0,
    medium: distribution.low,
    high: distribution.low + distribution.medium
  };

  // Convert sentiment to numerical value for positioning
  const getSentimentValue = (sentiment) => {
    switch (sentiment) {
      case 'negative':
        return 100; // Bottom side
      case 'neutral':
        return 50; // Middle
      case 'positive':
        return 0; // Top side
      default:
        return 50;
    }
  };

  // Calculate positions for each cluster
  const positionedClusters = sortedClusters.map((cluster) => {
    const range = getFrequencyRange(cluster.frequency);
    const rangeStart = cumulativeWidth[range];
    const rangeWidth = distribution[range];

    // Calculate position within its range
    const itemsInRange = groupedClusters[range].length;
    const itemIndex = groupedClusters[range].findIndex((c) => c.frequency === cluster.frequency);
    const xPos = rangeStart + (rangeWidth * (itemIndex + 0.5)) / itemsInRange;

    // Determine card position based on number of items in range and sentiment
    let cardPosition;
    if (itemsInRange === 1) {
      cardPosition = cluster.sentiment === 'positive' ? 'bottom' : 'top';
    } else {
      cardPosition =
        cluster.sentiment === 'neutral'
          ? cluster.frequency > maxFrequency * 0.7
            ? 'right'
            : 'left'
          : cluster.sentiment === 'positive'
          ? 'bottom'
          : 'top';
    }

    return {
      ...cluster,
      id: cluster.name,
      xPos,
      yPos: getSentimentValue(cluster.sentiment),
      cardPosition
    };
  });

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height: '600px',
        mt: 2,
        mb: 10,
        px: 6,
        py: 4
      }}
    >
      {/* Grid Container */}
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          height: '100%',
          border: '1px solid #e0e0e0'
        }}
      >
        {/* Y-axis label */}
        <Box
          sx={{
            position: 'absolute',
            left: -40,
            top: '50%',
            transform: 'rotate(-90deg) translateX(50%)',
            transformOrigin: 'left center',
            color: 'text.secondary',
            fontSize: '0.875rem'
          }}
        >
          Sentiment
        </Box>

        {/* Y-axis markers */}
        <Box
          sx={{
            position: 'absolute',
            left: -60,
            top: 0,
            bottom: 0,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            py: 2
          }}
        >
          <Typography variant="caption" color="text.secondary">
            Positive
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Negative
          </Typography>
        </Box>

        {/* X-axis labels */}
        <Box
          sx={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: -30,
            display: 'flex',
            px: 2
          }}
        >
          <Box sx={{ width: `${distribution.low}%` }}>
            <Typography variant="caption" color="text.secondary">
              Low Frequency
            </Typography>
          </Box>
          <Box sx={{ width: `${distribution.medium}%` }}>
            <Typography variant="caption" color="text.secondary">
              Medium Frequency
            </Typography>
          </Box>
          <Box sx={{ width: `${distribution.high}%` }}>
            <Typography variant="caption" color="text.secondary">
              High Frequency
            </Typography>
          </Box>
        </Box>

        {/* Grid lines */}
        <Box sx={{ position: 'absolute', inset: 0 }}>
          {/* Horizontal grid lines */}
          {[0, 1, 2, 3].map((i) => (
            <Box
              key={`h-${i}`}
              sx={{
                position: 'absolute',
                left: 0,
                right: 0,
                top: `${(i / 3) * 100}%`,
                borderTop: '1px solid #e0e0e0'
              }}
            />
          ))}
          {/* Vertical grid lines - based on distribution */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: `${distribution.low}%`,
              borderLeft: '1px solid #e0e0e0'
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: `${distribution.low + distribution.medium}%`,
              borderLeft: '1px solid #e0e0e0'
            }}
          />
        </Box>

        {/* Clusters */}
        {positionedClusters.map((cluster) => (
          <Box
            key={cluster.id}
            sx={{
              position: 'absolute',
              left: `${cluster.xPos}%`,
              top: `${cluster.yPos}%`,
              transform: 'translate(-50%, -50%)',
              zIndex: lastExpandedId === cluster.id ? 1200 : 1
            }}
          >
            {/* Node */}
            <Box
              sx={{
                width: '12px',
                height: '12px',
                borderRadius: '50%',
                backgroundColor: getNodeColor(cluster.sentiment),
                position: 'absolute',
                left: '50%',
                top: '50%',
                transform: 'translate(-50%, -50%)',
                zIndex: 2
              }}
            />

            {/* Card */}
            <ClusterCard
              cluster={cluster}
              position={cluster.cardPosition}
              isTopMost={lastExpandedId === cluster.id}
              onExpand={() => setLastExpandedId(cluster.id)}
              showQuotes={showQuotes}
            />
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const GridMatrixView = ({ clusters, maxFrequency, showQuotes }) => {
  // Helper function to categorize frequency
  const getFrequencyCategory = (frequency) => {
    const lowThreshold = maxFrequency * 0.33;
    const highThreshold = maxFrequency * 0.66;
    if (frequency <= lowThreshold) return 'low';
    if (frequency <= highThreshold) return 'medium';
    return 'high';
  };

  // Group clusters by frequency and sentiment
  const groupedClusters = clusters.reduce((acc, cluster) => {
    const freqCategory = getFrequencyCategory(cluster.frequency);
    const sentiment = cluster.sentiment;

    if (!acc[freqCategory]) acc[freqCategory] = {};
    if (!acc[freqCategory][sentiment]) acc[freqCategory][sentiment] = [];

    acc[freqCategory][sentiment].push(cluster);
    return acc;
  }, {});

  const frequencies = ['high', 'medium', 'low'];
  const sentiments = ['positive', 'neutral', 'negative'];

  // Function to get gradient background based on sentiment
  const getGradientBackground = (sentiment) => {
    switch (sentiment) {
      case 'positive':
        return 'linear-gradient(135deg, rgba(227, 252, 236, 0.6) 0%, rgba(227, 252, 236, 0.01) 100%)';
      case 'negative':
        return 'linear-gradient(135deg, rgba(253, 236, 234, 0.6) 0%, rgba(253, 236, 234, 0.01) 100%)';
      default: // neutral
        return 'linear-gradient(135deg, rgba(255, 248, 225, 0.6) 0%, rgba(255, 248, 225, 0.01) 100%)';
    }
  };

  // Function to get border color based on sentiment
  const getBorderColor = (sentiment) => {
    switch (sentiment) {
      case 'positive':
        return 'success.main';
      case 'negative':
        return 'error.main';
      default: // neutral
        return 'warning.main';
    }
  };

  return (
    <Box sx={{ mt: 4 }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 2
        }}
      >
        {frequencies.map((freq) =>
          sentiments.map((sent) => (
            <Card
              key={`${freq}-${sent}`}
              className="thematic-grid-card"
              data-sentiment={sent}
              sx={{
                p: 2,
                minHeight: 150,
                display: 'flex',
                flexDirection: 'column',
                background: getGradientBackground(sent)
              }}
            >
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {sent === 'neutral' ? 'Mixed' : sent.charAt(0).toUpperCase() + sent.slice(1)} •{' '}
                {freq.charAt(0).toUpperCase() + freq.slice(1)} Frequency
              </Typography>
              <Box sx={{ flex: 1 }}>
                {groupedClusters[freq]?.[sent]?.length > 0 ? (
                  groupedClusters[freq][sent].map((cluster, index) => (
                    <Box
                      key={index}
                      sx={{
                        mb: 1.5,
                        ml: 1,
                        pl: 2,
                        borderLeft: '3px solid',
                        borderColor: getBorderColor(sent),
                        py: 0.5
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                        {cluster.name} ({cluster.frequency} mentions)
                      </Typography>
                      {showQuotes && cluster.key_quotes && cluster.key_quotes[0] && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            display: 'block',
                            mt: 0.5
                          }}
                        >
                          &quot;{cluster.key_quotes[0]}&quot;
                        </Typography>
                      )}
                    </Box>
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No clusters in this category
                  </Typography>
                )}
              </Box>
            </Card>
          ))
        )}
      </Box>
    </Box>
  );
};

const ThematicClusters = () => {
  const [view, setView] = useState('matrix');
  const [showQuotes, setShowQuotes] = useState(true);
  const thematic_clusters = useSelector((state) => state.reviewsReducer.v2Data.review_analysis_results.thematic_clusters);

  if (!thematic_clusters) return null;

  // Format thematic clusters
  const clusters = Object.entries(thematic_clusters).map(([name, data]) => ({
    name,
    ...data
  }));

  // Find max frequency for percentage calculation
  const maxFrequency = Math.max(...clusters.map((c) => c.frequency));

  // Sort by frequency
  const sortedClusters = [...clusters].sort((a, b) => b.frequency - a.frequency);

  const handleViewChange = (event, newView) => {
    if (newView !== null) {
      setView(newView);
    }
  };

  return (
    <Card sx={{ width: '100%', mt: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5" gutterBottom sx={{ mb: 0 }}>
            Thematic Clusters
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <ToggleButton
              value="quotes"
              selected={showQuotes}
              onChange={() => setShowQuotes(!showQuotes)}
              size="small"
              sx={{
                height: 28,
                px: 1,
                fontSize: '0.75rem'
              }}
            >
              {showQuotes ? 'Hide Quotes' : 'Show Quotes'}
            </ToggleButton>
            <ToggleButtonGroup value={view} exclusive onChange={handleViewChange} size="small">
              <ToggleButton value="list" aria-label="list view">
                <TableChartOutlinedIcon />
              </ToggleButton>
              <ToggleButton value="matrix" aria-label="matrix view">
                <TimelineOutlinedIcon />
              </ToggleButton>
              <ToggleButton value="grid" aria-label="grid view">
                <GridViewIcon />
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Box>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Key themes and patterns identified in customer reviews
        </Typography>

        <Box sx={{ mt: 4 }}>
          {view === 'list' ? (
            <ClusterSection clusters={sortedClusters} maxFrequency={maxFrequency} showQuotes={showQuotes} />
          ) : view === 'matrix' ? (
            <MatrixView clusters={sortedClusters} maxFrequency={maxFrequency} showQuotes={showQuotes} />
          ) : (
            <GridMatrixView clusters={sortedClusters} maxFrequency={maxFrequency} showQuotes={showQuotes} />
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default ThematicClusters;
